import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { CheckIcon } from "@heroicons/react/solid";
import { Field, FormikHelpers, FormikProps } from "formik";
import produce from "immer";
import Tooltip from "rc-tooltip";
import React, { Fragment, useCallback, useMemo, useRef, useState } from "react";
import { NavLink, useHistory } from "react-router-dom";
import { useSWRConfig } from "swr";
import * as Yup from "yup";
import AlertMessage from "../../../../components/AlertMessage";
import FormProvider from "../../../../components/forms/FormProvider";
import Input from "../../../../components/forms/Input";
import InfoIcon from "../../../../components/icons/InfoIcon";
import UpgradeSimulationsModal from "../../../../components/modals/UpgradeSimulationsModal";
import { useBillingPlans } from "../../../../context/billingPlans";
import { useGoogleAnalytics } from "../../../../context/googleAnalytics";
import { useLocations } from "../../../../context/locations/LocationsProvider";
import { useToast } from "../../../../context/toast";
import { ModalConfig } from "../../../../context/toast/ToastProvider";
import { useWorkspaces } from "../../../../context/workspaces";
import { ApiResponse, fetchApi } from "../../../../utils/api";
import { formatDateToLocale } from "../../../../utils/dates";
import {
  HotspotLocation,
  PaymentStep,
  PlanType,
  SuperSimulation
} from "../../../../utils/types";
import useMapConfig from "../../../../utils/useMapConfig";

interface Props {
  location: HotspotLocation | undefined;
  open: boolean;
  toggle: () => void;
  upgradeSimulationsConfig: ModalConfig;
  setUpgradeSimulationsConfig: React.Dispatch<
    React.SetStateAction<ModalConfig>
  >;
}

const SuperSimulationSidebar: React.FC<Props> = ({
  location,
  open,
  toggle,
  upgradeSimulationsConfig,
  setUpgradeSimulationsConfig
}) => {
  const history = useHistory();
  const { mutate } = useSWRConfig();
  const { currentWorkspaceId, currentWorkspace, setWorkspaces } =
    useWorkspaces();
  const { trackEvent } = useGoogleAnalytics();
  const distanceUnit = useMapConfig((state) => state.distanceUnit);
  const { currentPlan, setPaymentStep, setNewPlan } = useBillingPlans();
  const getAppearanceDistanceValue = useMapConfig(
    (state) => state.getAppearanceDistanceValue
  );
  const getDistanceValueInMeters = useMapConfig(
    (state) => state.getDistanceValueInMeters
  );
  const renderDistance = useMapConfig((state) => state.renderDistance);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dialogRef = useRef(null);
  const { simulationsLimitExceeded } = useLocations();
  const { addToast } = useToast();

  const initialValues = useMemo(
    () => ({
      height: getAppearanceDistanceValue(
        location?.height || DEFAULT_SIMULATION_HEIGHT,
        false
      )
    }),
    [getAppearanceDistanceValue, location?.height]
  );

  const onSubmit = useCallback(
    async (
      formValues: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      if (!currentWorkspaceId || !location) return;
      setErrorMessage(null);

      trackEvent("super simulation button", {
        event_category: "simulations",
        event_label: "super simulation button"
      });

      const height = getDistanceValueInMeters(formValues.height);

      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}/locations-simulations`,
          {
            method: "POST",
            body: JSON.stringify({ locationUuid: location.uuid, height })
          }
        );

        const apiResponse: ApiResponse<SuperSimulation> = await response.json();

        if (apiResponse.status) {
          mutate(location.uuid); // Trigger fetching the location details again, containing this new simulation

          setWorkspaces((prevState) =>
            produce(prevState, (draft) => {
              if (!draft[currentWorkspaceId].active_plan) return;

              draft[
                currentWorkspaceId
              ].active_plan.simulations_used_on_period += 1;
            })
          );

          toggle();
          history.push(
            `/locations/${location.uuid}/super-simulations/${apiResponse.data.uuid}`
          );
        } else {
          addToast({
            type: "error",
            message: apiResponse.message || "Failed to generate simulations"
          });
        }
      } catch (e) {
        console.error(e);
        formikHelpers.setSubmitting(false);
        addToast({
          type: "error",
          message: "Failed to generate simulations"
        });
      }
    },
    [
      currentWorkspaceId,
      location,
      trackEvent,
      getDistanceValueInMeters,
      mutate,
      setWorkspaces,
      toggle,
      history,
      addToast
    ]
  );

  const limitExceededAlertMessage = useMemo(() => {
    if (!currentWorkspace || !currentWorkspace.active_plan) return null;

    const { allowed_simulations, simulations_used_on_period } =
      currentWorkspace.active_plan;

    const availableSuperSimulations =
      allowed_simulations - simulations_used_on_period;

    if (currentPlan?.type === PlanType.community) {
      return (
        <AlertMessage
          type="warning"
          title={
            <div className="flex flex-col">
              <span className="font-bold">{currentPlan?.name} Plan</span>

              {simulations_used_on_period === 0 ? (
                <span>
                  This workspace has{" "}
                  <b className="text-hotspotty-300">{allowed_simulations}</b>{" "}
                  super simulation per month. You will use this one simulation
                  credit.
                </span>
              ) : (
                <span>
                  You have one free super simulation per month but it has
                  already been used. You can increase this limit by{" "}
                  <NavLink
                    className="font-bold text-hotspotty-300 hover:underline"
                    onClick={() => {
                      toggle();
                      setPaymentStep(PaymentStep.plans);
                    }}
                    to="/pricing"
                  >
                    upgrading your plan
                  </NavLink>
                  . Alternatively you can wait until your simulation credit
                  resets next month.
                </span>
              )}
            </div>
          }
        />
      );
    }

    return (
      <AlertMessage
        type="warning"
        title={
          <div className="flex flex-col">
            <span>
              Your workspace has{" "}
              <b className="text-hotspotty-300">{availableSuperSimulations}</b>{" "}
              out of <b className="text-hotspotty-300">{allowed_simulations}</b>{" "}
              super simulations left for this billing period.
            </span>

            {availableSuperSimulations === 0 ? (
              <div className="flex flex-col mt-2">
                <span>
                  You can <b className="text-hotspotty-300">not</b> run this
                  super simulation. Here are your options:
                </span>
                <ul className="my-4 space-y-4">
                  <li className="flex space-x-3">
                    <CheckIcon
                      className="flex-shrink-0 h-5 w-5 text-green-500"
                      aria-hidden="true"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-300">
                      Wait until your super simulations reset on{" "}
                      {formatDateToLocale(
                        currentWorkspace.active_plan.next_billing_date,
                        true
                      )}
                    </span>
                  </li>
                  <li className="flex space-x-3">
                    <CheckIcon
                      className="flex-shrink-0 h-5 w-5 text-green-500"
                      aria-hidden="true"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-300">
                      Increase the monthly super simulations limit and proceed
                      immediately
                    </span>
                  </li>
                </ul>
                <button
                  type="button"
                  className="mt-4 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
                  onClick={() => {
                    setNewPlan((prevState) => ({
                      ...prevState,
                      simulationAmount: allowed_simulations + 1
                    }));

                    setUpgradeSimulationsConfig({ open: true });
                  }}
                >
                  Upgrade super simulations
                </button>
              </div>
            ) : (
              <span>
                You will use <b className="text-hotspotty-300">1</b> simulation
                credit.
              </span>
            )}
          </div>
        }
      />
    );
  }, [
    currentWorkspace,
    currentPlan?.type,
    currentPlan?.name,
    toggle,
    setPaymentStep,
    setNewPlan,
    setUpgradeSimulationsConfig
  ]);

  return (
    <>
      <UpgradeSimulationsModal
        config={upgradeSimulationsConfig}
        setConfig={setUpgradeSimulationsConfig}
      />

      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          static
          className="fixed inset-0 z-20 overflow-hidden"
          open={open}
          onClose={() => {
            toggle();
          }}
          initialFocus={dialogRef}
        >
          <div className="absolute inset-0 overflow-hidden">
            <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="fixed inset-y-0 pl-16 max-w-full right-0 flex">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <div className="relative w-screen max-w-md h-full flex flex-col">
                  <FormProvider
                    classnames={{ wrapper: "xl:max-w-7xl h-full" }}
                    onCancel={toggle}
                    error={errorMessage}
                    disabled={simulationsLimitExceeded}
                    submitButtonText="Run simulation"
                    submittingSubmitButtonText="Processing..."
                    formikProps={{
                      onSubmit,
                      initialValues,
                      validationSchema: Yup.object().shape({
                        height: Yup.number().nullable().min(0).max(2800) // 2800ft for the burj khalifa
                      })
                    }}
                    orderedFieldNames={ORDERED_FIELD_NAMES}
                  >
                    {({ isSubmitting }: FormikProps<FormValues>) => {
                      return (
                        <div className="flex-1 h-0 overflow-y-auto">
                          <div className="flex-shrink-0 py-6 px-4 bg-purple-700 sm:px-6 dark:bg-gray-800">
                            <div className="flex items-center justify-between">
                              <Dialog.Title className="text-lg font-medium text-white">
                                Simulate witnesses
                              </Dialog.Title>
                              <div className="ml-3 h-7 flex items-center">
                                <button
                                  ref={dialogRef}
                                  type="button"
                                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:bg-gray-800 dark:hover:text-white"
                                  onClick={toggle}
                                >
                                  <span className="sr-only">Close panel</span>
                                  <XIcon
                                    className="h-6 w-6"
                                    aria-hidden="true"
                                  />
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="flex-1 flex flex-col justify-between">
                            <div className="px-4 sm:px-6">
                              <div className="space-y-6 pt-6 pb-5">
                                {limitExceededAlertMessage}
                                <p className="text-sm font-normal text-gray-900 dark:text-white">
                                  Simulate how a hotspot could perform if it
                                  were installed at this location at a given
                                  height above the ground.
                                </p>

                                <Field
                                  id="height"
                                  name="height"
                                  label={
                                    <>
                                      Installation height
                                      <Tooltip
                                        placement="top"
                                        overlay="Antenna height above the ground"
                                      >
                                        <InfoIcon className="mx-1 inline-flex h-4 w-4" />
                                      </Tooltip>
                                      ({distanceUnit})
                                    </>
                                  }
                                  placeholder={renderDistance(
                                    DEFAULT_SIMULATION_HEIGHT,
                                    false
                                  )}
                                  disabled={
                                    isSubmitting || simulationsLimitExceeded
                                  }
                                  type="number"
                                  component={Input}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  </FormProvider>
                </div>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default SuperSimulationSidebar;

//
// Utils
//

interface FormValues {
  height: number;
}

const ORDERED_FIELD_NAMES = ["height"];

export const DEFAULT_SIMULATION_HEIGHT = 10;
